import { useRef, useState } from "react";
import { Eye, EyeOff, FileText, ImageOff, Loader2, Mic, Pause, Play, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { MediaLightbox, type VisorItem } from "@/components/ui/media-lightbox";
import { esVisible, type MediaTipo } from "@/components/ui/media-tipo";
import { IconTip } from "@/components/ui/icon-tip";
import { clicVerDocumento } from "@/hooks/useVisorDocumento";

/**
 * Miniaturas de evidencia (foto, video con ▶, audio con play, documento con ícono) y la
 * galería que las junta con el visor.
 *
 * Nacieron en la evidencia del Portal de Tickets (`TicketEvidencia`); cobranza y
 * autorizaciones las reusan, así que viven aquí. `TicketEvidencia` las sigue re-exportando.
 */

// Franja de visibilidad al pie del tile: dice de un vistazo si el archivo llega a la app
// del cliente. Va encima de la miniatura, por eso el fondo opaco.
const BADGE_VISIBILIDAD_CLS =
  "absolute inset-x-0 bottom-0 z-10 flex h-[15px] items-center justify-center gap-1 text-[9px] font-semibold leading-none";
const BADGE_ON_CLS = "bg-primary text-primary-foreground";
const BADGE_OFF_CLS = "bg-black/65 text-white";

// Tile uniforme (96×96). Foto/video abren el visor (onOpen); audio se reproduce en el tile;
// documento abre/descarga en pestaña nueva. Botón de quitar/borrar al hover.
export function EvidenciaTile({
  src,
  tipo,
  nombre,
  onRemove,
  onOpen,
  onOpenDoc,
  busy,
  visibleCliente,
  onToggleVisible,
  cambiandoVisible,
}: {
  src: string;
  tipo: MediaTipo;
  nombre?: string;
  onRemove?: () => void;
  onOpen?: () => void;
  onOpenDoc?: () => void;
  busy?: boolean;
  /** Si viene, el tile pinta la franja "Cliente"/"Interno"; si no, no se pinta nada. */
  visibleCliente?: boolean;
  /** Si viene, la franja es un botón que cambia `visible_cliente` de esa fila. */
  onToggleVisible?: () => void;
  cambiandoVisible?: boolean;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  // Foto que no carga (archivo borrado o URL caducada): un ícono con su nombre, no el ícono roto
  // del navegador con el alt encima.
  const [rota, setRota] = useState(false);

  const toggleAudio = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      void a.play();
      setPlaying(true);
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="group relative size-24 shrink-0 overflow-hidden rounded-lg border border-border bg-muted/40">
      {tipo === "foto" && (
        <IconTip label={nombre || "Foto"}>
          <button type="button" onClick={onOpen} className="block size-full">
            {rota ? (
              <span className="flex size-full flex-col items-center justify-center gap-1 px-1.5 text-center text-muted-foreground">
                <ImageOff className="size-5" aria-hidden />
                <span className="line-clamp-2 break-all text-[10px] leading-tight">{nombre || "Foto"}</span>
                <span className="text-[9px]">No se pudo cargar</span>
              </span>
            ) : (
              <img src={src} alt={nombre || "foto"} className="size-full object-cover" onError={() => setRota(true)} />
            )}
          </button>
        </IconTip>
      )}

      {tipo === "video" && (
        <IconTip label={nombre || "Video"}>
          <button type="button" onClick={onOpen} className="block size-full">
            <video src={src} className="size-full object-cover" muted playsInline preload="metadata" />
            <span className="pointer-events-none absolute inset-0 grid place-items-center">
              <span className="grid size-8 place-items-center rounded-full bg-black/55 text-white">
                <Play className="size-4 translate-x-px fill-current" />
              </span>
            </span>
          </button>
        </IconTip>
      )}

      {tipo === "audio" && (
        <IconTip label={nombre || "Nota de voz"}>
          <button
            type="button"
            onClick={toggleAudio}
         
            className="flex size-full flex-col items-center justify-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className="grid size-9 place-items-center rounded-full bg-primary/10 text-primary">
              {playing ? <Pause className="size-4" /> : <Play className="size-4 translate-x-px fill-current" />}
            </span>
            <span className="flex items-center gap-1 text-[10px]">
              <Mic className="size-3" /> Voz
            </span>
            <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)} className="hidden" />
          </button>
        </IconTip>
      )}

      {tipo === "documento" &&
        (onOpenDoc ? (
          <IconTip label={nombre || "Documento"}>
            <button
              type="button"
              onClick={onOpenDoc}
           
              className="flex size-full flex-col items-center justify-center gap-1 p-1.5 text-center text-muted-foreground transition-colors hover:text-foreground"
            >
              <FileText className="size-7" />
              <span className="line-clamp-2 break-all text-[9px] leading-tight">{nombre || "Documento"}</span>
            </button>
          </IconTip>
        ) : (
          <IconTip label={nombre || "Documento"}>
            <a
              href={src}
              onClick={clicVerDocumento(src, nombre || "Documento")}
           
              className="flex size-full flex-col items-center justify-center gap-1 p-1.5 text-center text-muted-foreground transition-colors hover:text-foreground"
            >
              <FileText className="size-7" />
              <span className="line-clamp-2 break-all text-[9px] leading-tight">{nombre || "Documento"}</span>
            </a>
          </IconTip>
        ))}

      {visibleCliente !== undefined &&
        (onToggleVisible ? (
          <IconTip
            label={
              visibleCliente
                ? "El cliente lo ve en su app. Clic para dejarlo solo para el equipo."
                : "Solo lo ve el equipo. Clic para mostrarlo al cliente."
            }
          >
            <button
              type="button"
              onClick={onToggleVisible}
              disabled={cambiandoVisible}
              aria-label={visibleCliente ? "Ocultar al cliente" : "Mostrar al cliente"}
              className={cn(BADGE_VISIBILIDAD_CLS, "transition-opacity hover:opacity-90 disabled:opacity-60", visibleCliente ? BADGE_ON_CLS : BADGE_OFF_CLS)}
            >
              {cambiandoVisible ? (
                <Loader2 className="size-3 animate-spin" />
              ) : visibleCliente ? (
                <Eye className="size-3" />
              ) : (
                <EyeOff className="size-3" />
              )}
              {visibleCliente ? "Cliente" : "Interno"}
            </button>
          </IconTip>
        ) : (
          <IconTip label={visibleCliente ? "El cliente lo ve en su app." : "Solo lo ve el equipo."}>
            <span
              tabIndex={0}
              className={cn(BADGE_VISIBILIDAD_CLS, visibleCliente ? BADGE_ON_CLS : BADGE_OFF_CLS)}
            >
              {visibleCliente ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
              {visibleCliente ? "Cliente" : "Interno"}
            </span>
          </IconTip>
        ))}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          aria-label="Quitar"
          className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
        </button>
      )}
    </div>
  );
}

export interface EvidenciaGaleriaItem {
  id: string | number;
  src: string;
  tipo: MediaTipo;
  nombre?: string;
  /** Si viene, la miniatura dice si el cliente lo ve en su app ("Cliente") o no ("Interno"). */
  visibleCliente?: boolean;
}

/**
 * Galería de solo lectura: miniaturas uniformes y, al tocar una foto o video, el visor en la
 * misma pantalla con ‹ › entre los archivos de la galería. Audio se reproduce en la miniatura y
 * los documentos se abren en otra pestaña.
 */
export function EvidenciaGaleria({
  items,
  className,
}: {
  items: EvidenciaGaleriaItem[];
  className?: string;
}) {
  const [viewer, setViewer] = useState<number | null>(null);
  const visor: VisorItem[] = items
    .filter((x) => esVisible(x.tipo))
    .map((x) => ({ src: x.src, tipo: x.tipo, nombre: x.nombre }));

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {items.map((x) => (
        <EvidenciaTile
          key={x.id}
          src={x.src}
          tipo={x.tipo}
          nombre={x.nombre}
          visibleCliente={x.visibleCliente}
          onOpen={esVisible(x.tipo) ? () => setViewer(visor.findIndex((v) => v.src === x.src)) : undefined}
        />
      ))}
      {viewer !== null && visor[viewer] && (
        <MediaLightbox items={visor} index={viewer} onClose={() => setViewer(null)} onIndex={setViewer} />
      )}
    </div>
  );
}
